const FOCUSABLE_SELECTORS = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
const TIMER_DURATION_MS = 10 * 60 * 1000;
const TIMER_STORAGE_KEY = 'cartReservationTimer';

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.previousActiveElement = null;
    this.isOpen = false;
    this.focusTrapHandler = null;
    this.toastTimer = null;
    this.handleKeydown = null;

    this.isTimerEnabled = this.dataset.timerEnabled === 'true';
    this.timerStartTime = null;
    this.timerInterval = null;
  }

  connectedCallback() {
    this.overlay = this.querySelector('[data-cart-overlay]');
    this.panel = this.querySelector('[data-cart-panel]');
    this.maxQuantity = parseInt(this.dataset.maxQuantity, 10) || 5;

    this.overlay?.addEventListener('click', () => this.close());

    this.addEventListener('click', (event) => {
      const { target } = event;

      if (target.closest('[data-cart-close]')) {
        this.close();
      } else if (target.closest('[data-quantity-minus]')) {
        this.handleQuantityChange(target.closest('[data-quantity-minus]'), -1);
      } else if (target.closest('[data-quantity-plus]')) {
        this.handleQuantityChange(target.closest('[data-quantity-plus]'), 1);
      } else if (target.closest('[data-checkout-button]')) {
        this.handleCheckout(event, target.closest('[data-checkout-button]'));
      } else if (target.closest('[data-cart-toast-close]')) {
        this.hideToast();
      }
    });

    this.handleCartTrigger = (event) => {
      if (event.target.closest('.cart-trigger')) {
        event.preventDefault();
        this.open();
      }
    };
    document.addEventListener('click', this.handleCartTrigger);

    this.unsubscribeCartAdd = subscribe(PUB_SUB_EVENTS.cartAdd, (data) => {
      this.renderContents(data);
      if (!this.isOpen) this.open();
    });

    this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, (data) => {
      this.renderContents(data);
    });

    this.unsubscribeCartError = subscribe(PUB_SUB_EVENTS.cartError, async ({ source, error }) => {
      if (source !== 'cart-drawer' || !error) return;
      this.showToast(error.description ?? error.message ?? 'Failed to update cart.');
      await this.refetchSection();
    });

    if (this.isTimerEnabled) {
      this.initTimer();
      const cartItems = this.querySelector('[data-cart-items]');
      if (cartItems?.children.length > 0 && !sessionStorage.getItem(TIMER_STORAGE_KEY)) {
        this.startTimer();
      }
    }
  }

  disconnectedCallback() {
    this.unsubscribeCartAdd?.();
    this.unsubscribeCartUpdate?.();
    this.unsubscribeCartError?.();
    document.removeEventListener('click', this.handleCartTrigger);
    this.removeKeydownListener();
    this.removeTrapFocus();
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  // --- Timer methods ---

  initTimer() {
    const timerEl = this.querySelector('[data-cart-reservation-timer]');
    const displayEl = this.querySelector('[data-reservation-timer-display]');
    if (!timerEl || !displayEl) return;

    const stored = sessionStorage.getItem(TIMER_STORAGE_KEY);
    if (!stored) return;

    const startTime = parseInt(stored, 10);
    const elapsed = Date.now() - startTime;

    if (elapsed < TIMER_DURATION_MS) {
      this.timerStartTime = startTime;
      this.showTimer();
    } else {
      sessionStorage.removeItem(TIMER_STORAGE_KEY);
      this.timerStartTime = null;
    }
  }

  startTimer() {
    const timerEl = this.querySelector('[data-cart-reservation-timer]');
    const displayEl = this.querySelector('[data-reservation-timer-display]');
    if (!timerEl || !displayEl) return;

    if (this.timerStartTime && sessionStorage.getItem(TIMER_STORAGE_KEY)) return;

    this.timerStartTime = Date.now();
    sessionStorage.setItem(TIMER_STORAGE_KEY, this.timerStartTime.toString());
    this.showTimer();
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    this.timerStartTime = null;
    sessionStorage.removeItem(TIMER_STORAGE_KEY);
    this.hideTimer();
  }

  showTimer() {
    const timerEl = this.querySelector('[data-cart-reservation-timer]');
    if (!timerEl) return;

    timerEl.classList.remove('max-h-0');
    timerEl.classList.add('max-h-[200px]');

    this.updateTimerDisplay();

    if (!this.timerInterval) {
      this.timerInterval = setInterval(() => this.updateTimerDisplay(), 1000);
    }
  }

  hideTimer() {
    const timerEl = this.querySelector('[data-cart-reservation-timer]');
    if (!timerEl) return;

    timerEl.classList.remove('max-h-[200px]');
    timerEl.classList.add('max-h-0');

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  updateTimerDisplay() {
    if (!this.timerStartTime) return;
    const displayEl = this.querySelector('[data-reservation-timer-display]');
    if (!displayEl) return;

    const elapsed = Date.now() - this.timerStartTime;
    const remaining = Math.max(0, TIMER_DURATION_MS - elapsed);

    if (remaining <= 0) {
      this.timerStartTime = Date.now();
      sessionStorage.setItem(TIMER_STORAGE_KEY, this.timerStartTime.toString());
      return;
    }

    const minutes = Math.floor(remaining / 60_000);
    const seconds = Math.floor((remaining % 60_000) / 1000);
    displayEl.textContent = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  // --- Drawer open/close ---

  open() {
    this.previousActiveElement = document.activeElement;
    // this.hidden = false;
    // this.isOpen = true;

    // requestAnimationFrame(() => {
    //   requestAnimationFrame(() => {
    //     this.overlay?.classList.remove('opacity-0');
    //     this.overlay?.classList.add('opacity-100');

    //     this.panel?.classList.remove('translate-y-full', 'lg:translate-x-full');
    //     this.panel?.classList.add('translate-y-0', 'lg:translate-x-0');

    //     document.body.classList.add('overflow-hidden');
    //     this.setAttribute('aria-hidden', 'false');

    //     this.trapFocus();
    //   });
    // });

    // this.addKeydownListener();

    window.handleFloCartBtn?.();

    if (this.isTimerEnabled) {
      this.initTimer();
    }
  }

  close() {
    this.isOpen = false;
    this.removeTrapFocus();
    this.removeKeydownListener();

    this.overlay?.classList.remove('opacity-100');
    this.overlay?.classList.add('opacity-0');

    this.panel?.classList.remove('translate-y-0', 'lg:translate-x-0');
    this.panel?.classList.add('translate-y-full', 'lg:translate-x-full');

    document.body.classList.remove('overflow-hidden');
    this.setAttribute('aria-hidden', 'true');

    setTimeout(() => {
      this.hidden = true;
      this.previousActiveElement?.focus();
    }, 300);
  }

  addKeydownListener() {
    if (this.handleKeydown) return;
    this.handleKeydown = (event) => {
      if (event.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this.handleKeydown);
  }

  removeKeydownListener() {
    if (!this.handleKeydown) return;
    document.removeEventListener('keydown', this.handleKeydown);
    this.handleKeydown = null;
  }

  trapFocus() {
    if (!this.panel) return;
    const focusable = [...this.panel.querySelectorAll(FOCUSABLE_SELECTORS)];
    if (!focusable.length) return;

    const firstFocusable = focusable[0];
    const lastFocusable = focusable[focusable.length - 1];

    firstFocusable.focus();
    this.removeTrapFocus();

    this.focusTrapHandler = (event) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    this.panel.addEventListener('keydown', this.focusTrapHandler);
  }

  removeTrapFocus() {
    if (this.focusTrapHandler && this.panel) {
      this.panel.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }

  // --- Cart rendering ---

  renderContents(data) {
    const sectionId = this.dataset.sectionId;
    const sectionHtml = data.sections?.[sectionId] ?? data.sections?.['cart-drawer'];
    if (!sectionHtml) return;

    const template = document.createElement('template');
    template.innerHTML = sectionHtml;
    const newContent = template.content.querySelector('cart-drawer');
    if (!newContent) return;

    resetShimmer(this);
    morph(this, newContent, { ...MORPH_OPTIONS, childrenOnly: true });

    this.overlay = this.querySelector('[data-cart-overlay]');
    this.panel = this.querySelector('[data-cart-panel]');

    if (this.isOpen) {
      this.overlay?.classList.remove('opacity-0');
      this.overlay?.classList.add('opacity-100');
      this.panel?.classList.remove('translate-y-full', 'lg:translate-x-full');
      this.panel?.classList.add('translate-y-0', 'lg:translate-x-0');
    }

    const itemCount = data.cartData?.item_count ?? data.item_count ?? (parseInt(newContent.dataset.cartItemCount, 10) || 0);
    updateCartCount(itemCount);

    if (this.isTimerEnabled) {
      if (itemCount === 0) {
        this.stopTimer();
      } else {
        this.initTimer();
        if (!this.timerStartTime) {
          this.startTimer();
        }
      }
    }
  }

  async refetchSection() {
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const sectionId = this.dataset.sectionId;
      const response = await fetch(`${root}?sections=${sectionId}`);
      if (!response.ok) {
        resetShimmer(this);
        return;
      }
      const sections = await response.json();
      this.renderContents({ sections });
    } catch {
      // Recovery fetch failed — clear shimmer so UI is not stuck in loading state
      resetShimmer(this);
    }
  }

  // --- Quantity changes ---

  handleQuantityChange(button, delta) {
    const lineIndex = parseInt(button.dataset.lineIndex, 10);
    const key = button.dataset.lineKey;
    const input = this.querySelector(`[data-quantity-input][data-line-index="${lineIndex}"]`);
    if (!input || !key) return;

    const currentQty = parseInt(input.value, 10);
    const newQty = Math.max(0, Math.min(this.maxQuantity, currentQty + delta));
    if (newQty === currentQty) return;

    CartStore.saveSnapshot(key, currentQty, CartStore.cartItemCount);

    input.value = newQty;
    updateCartCount(CartStore.cartItemCount + delta);

    if (newQty === 0) {
      this.animateItemRemoval(lineIndex);
    } else {
      const minusBtn = this.querySelector(`[data-quantity-minus][data-line-index="${lineIndex}"]`);
      if (minusBtn) {
        minusBtn.querySelector('[data-icon-delete]')?.classList.toggle('hidden', newQty > 1);
        minusBtn.querySelector('[data-icon-minus]')?.classList.toggle('hidden', newQty <= 1);
      }

      const plusBtn = this.querySelector(`[data-quantity-plus][data-line-index="${lineIndex}"]`);
      if (plusBtn) {
        const atMax = newQty >= this.maxQuantity;
        plusBtn.disabled = atMax;
        plusBtn.classList.toggle('opacity-30', atMax);
        plusBtn.classList.toggle('cursor-not-allowed', atMax);
        plusBtn.classList.toggle('hover:bg-gray-100', !atMax);
      }

      this.querySelector(`#CartItem-${lineIndex} text-component[data-line-total-value]`)?.shimmer();
      this.querySelector('text-component[data-cart-subtotal]')?.shimmer();
    }

    CartStore.changeQuantity(key, newQty, [this.dataset.sectionId], 'cart-drawer')
      .catch(() => {});
  }

  animateItemRemoval(lineIndex) {
    const cartItem = this.querySelector(`#CartItem-${lineIndex}`);
    if (!cartItem) return;

    const parentLineKey = cartItem.dataset.lineKey;
    const childItems = parentLineKey
      ? Array.from(
        this.querySelectorAll(
          `.cart-item[data-child-line][data-parent-line-key="${parentLineKey}"]:not(.is-removing)`
        )
      )
      : [];

    const allItems = this.querySelectorAll('.cart-item:not(.is-removing)');
    const remainingAfterRemoval = allItems.length - 1 - childItems.length;

    if (remainingAfterRemoval <= 0) {
      this.showEmptyState();
      return;
    }

    animateRowRemoval(cartItem);
    childItems.forEach((child) => animateRowRemoval(child));
  }

  showEmptyState() {
    const template = this.querySelector('#cart-drawer-empty-template');
    if (!(template instanceof HTMLTemplateElement)) return;

    if (this.isTimerEnabled) this.stopTimer();

    const itemsContainer = this.querySelector('[data-cart-items]');
    const footer = this.querySelector('[data-cart-footer]');
    const clone = document.importNode(template.content, true);

    itemsContainer?.replaceWith(clone);
    footer?.remove();

    if (this.isTimerEnabled) {
      this.stopTimer();
    }
  }

  // --- Toast ---

  showToast(message) {
    const toast = this.querySelector('[data-cart-toast]');
    const messageEl = toast?.querySelector('[data-cart-toast-message]');
    if (!toast || !messageEl) return;

    clearTimeout(this.toastTimer);
    messageEl.textContent = message;
    toast.hidden = false;

    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    this.toastTimer = setTimeout(() => this.hideToast(), 3000);
  }

  hideToast() {
    const toast = this.querySelector('[data-cart-toast]');
    if (!toast) return;

    clearTimeout(this.toastTimer);
    toast.classList.remove('is-visible');

    setTimeout(() => {
      toast.hidden = true;
    }, 250);
  }

  // --- Checkout ---

  async handleCheckout(event, button) {
    const href = button.getAttribute('href');

    if (!CartStore.hasPendingChanges() && !CartStore.pendingRequest) {
      return;
    }

    event.preventDefault();

    const textEl = button.querySelector('[data-checkout-text]');
    const loaderEl = button.querySelector('[data-checkout-loader]');
    if (textEl) textEl.classList.add('invisible');
    if (loaderEl) {
      loaderEl.classList.remove('hidden');
      loaderEl.classList.add('flex');
    }

    await CartStore.flushPending([this.dataset.sectionId]);

    window.location.href = href;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
